import { keepPreviousData } from "@tanstack/react-query";
import React, { useState } from "react";

import { Button, Label, Small } from "@fronterahealth/frontera-ui-components";

import {
  EhrSessionDataSummaryPartsFragment,
  LearnerEhrSessionDataQueryVariables,
  useLearnerEhrSessionDataQuery,
} from "@api/graphql/types-and-hooks";
import { EhrStoredRowTable } from "@pages/ClientEhrProgramming/EhrStoredRowTable";
import { describeSessionSummary } from "@pages/ClientEhrProgramming/sessionDataSummary";

export const SESSION_DATA_PAGE_SIZE = 50;

export type EhrSessionDataScope = Pick<LearnerEhrSessionDataQueryVariables, "targetId" | "goalId" | "unmatched">;

interface EhrSessionDataSectionProps {
  learnerId: string;
  scope: EhrSessionDataScope;
  summary: EhrSessionDataSummaryPartsFragment;
  label?: string;
}

// Data points load only once the section is opened; they can number in the hundreds per target.
export const EhrSessionDataSection: React.FC<EhrSessionDataSectionProps> = ({
  learnerId,
  scope,
  summary,
  label = "Session data",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [offset, setOffset] = useState(0);

  const { data, isFetching, isError } = useLearnerEhrSessionDataQuery(
    { learnerId, ...scope, offset, limit: SESSION_DATA_PAGE_SIZE },
    {
      enabled: isOpen,
      queryKey: ["learner-ehr-session-data", learnerId, scope, offset],
      placeholderData: keepPreviousData,
    },
  );

  if (!summary.dataPointCount) return null;

  const page = data?.learnerEhrSessionData;
  const rows = (page?.rows || []).map((row) => ({
    id: row.id,
    fields: [
      ...(scope.unmatched
        ? [
            { key: "target_id", value: row.targetId },
            { key: "goal_id", value: row.goalId },
          ]
        : []),
      ...row.fields,
      ...(row.session?.fields || []).map((field) => ({ ...field, key: `session.${field.key}` })),
    ],
  }));
  const totalCount = page?.totalCount ?? summary.dataPointCount;
  const lastShown = Math.min(offset + SESSION_DATA_PAGE_SIZE, totalCount);

  let content: React.ReactNode = <Small className="text-text-secondary">Loading...</Small>;
  if (isError) {
    content = <Small className="text-text-secondary">Couldn&apos;t load session data.</Small>;
  } else if (page) {
    content = (
      <>
        <EhrStoredRowTable rows={rows} />
        <div className="flex items-center gap-3">
          <Small className="text-text-secondary">
            {offset + 1}–{lastShown} of {totalCount}
          </Small>
          <Button
            variant="primary"
            buttonStyle="border"
            text="Newer"
            disabled={offset === 0 || isFetching}
            onClick={() => setOffset(Math.max(0, offset - SESSION_DATA_PAGE_SIZE))}
          />
          <Button
            variant="primary"
            buttonStyle="border"
            text="Older"
            disabled={lastShown >= totalCount || isFetching}
            onClick={() => setOffset(offset + SESSION_DATA_PAGE_SIZE)}
          />
        </div>
      </>
    );
  }

  return (
    <details
      className="rounded-md border border-limestone-100 px-4 py-3"
      onToggle={(event) => setIsOpen(event.currentTarget.open)}
    >
      <summary className="cursor-pointer">
        <Label size="small">
          {label} ({describeSessionSummary(summary)})
        </Label>
      </summary>
      <div className="flex flex-col gap-3 pt-3">{content}</div>
    </details>
  );
};
